import React, { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { FaArrowDown, FaArrowUp } from "react-icons/fa";
import {
  createPageView,
  fetchUserEditableTemplates,
  saveUserEmailTemplateOrder,
} from "../../store/actions/users/users.actions";
import {
  resetUserEditableTemplateAPI,
  saveUserEditableTemplateAPI,
} from "../../utils/requests/userEmailTemplates";
import { toastError, toastSuccess } from "../../utils/helpers/helper";
import Spinner from "../../components/spinner/Spinner";
import "../../components/ProposalForm/index.css";

const MERGE_FIELD_TOKENS = [
  "[project name]",
  "[company name]",
  "[prospect company name]",
  "[name]",
  "[your name]",
];

const reorderList = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const EmailTemplates = () => {
  const dispatch = useDispatch();
  const { list, loading } = useSelector((state) => state.userEditableTemplates);
  const { user: profile } = useSelector((state) => state.userDetails);
  const mergeFields = [
    { token: "[project name]", value: "The project you are emailing about" },
    { token: "[company name]", value: profile?.company_name || "Your company name" },
    { token: "[prospect company name]", value: "The company you are emailing" },
    { token: "[name]", value: "The recipient's first name" },
    { token: "[your name]", value: profile?.first_name || "Your first name" },
  ];
  const [selectedKey, setSelectedKey] = useState(null);
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [activeField, setActiveField] = useState("body");
  const [saving, setSaving] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  useEffect(() => {
    dispatch(fetchUserEditableTemplates());
    dispatch(createPageView("Email Templates", "email_templates", "Email Templates"));
  }, [dispatch]);

  useEffect(() => {
    if (!list?.length) {
      return;
    }
    if (!selectedKey || !list.some((template) => template.key === selectedKey)) {
      const first = list[0];
      setSelectedKey(first.key);
      setName(first.name || "");
      setSubject(first.subject || "");
      setBody(first.body || "");
    }
  }, [list, selectedKey]);

  const selected = list.find((template) => template.key === selectedKey);

  const persistOrder = (nextList) => {
    setSavingOrder(true);
    dispatch(saveUserEmailTemplateOrder(nextList.map((template) => template.key)))
      .then(() => toastSuccess("Template order saved"))
      .catch(() => toastError("Unable to save template order"))
      .finally(() => setSavingOrder(false));
  };

  const move = (index, direction) => {
    const endIndex = index + direction;
    if (endIndex < 0 || endIndex >= list.length) {
      return;
    }
    persistOrder(reorderList(list, index, endIndex));
  };

  const handleSelect = (template) => {
    setSelectedKey(template.key);
    setName(template.name || "");
    setSubject(template.subject || "");
    setBody(template.body || "");
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!selectedKey) {
      return;
    }
    setSaving(true);
    saveUserEditableTemplateAPI(selectedKey, body, name, subject)
      .then(() => {
        toastSuccess("Template saved");
        dispatch(fetchUserEditableTemplates());
      })
      .catch(() => toastError("Unable to save template"))
      .finally(() => setSaving(false));
  };

  const handleReset = () => {
    if (!selectedKey) {
      return;
    }
    setSaving(true);
    resetUserEditableTemplateAPI(selectedKey)
      .then((res) => {
        setName(res?.name || selected?.default_name || "");
        setSubject(res?.subject || selected?.default_subject || "");
        setBody(res?.body || selected?.default_body || "");
        toastSuccess("Template reset to default");
        dispatch(fetchUserEditableTemplates());
      })
      .catch(() => toastError("Unable to reset template"))
      .finally(() => setSaving(false));
  };

  const insertMergeField = (field) => {
    const append = (current) => `${current || ""}${current ? " " : ""}${field}`;
    if (activeField === "subject") {
      setSubject(append);
      return;
    }
    setBody(append);
  };

  return (
    <Container className="py-4">
      <h2 className="mb-2">Email Templates</h2>
      <p className="text-muted mb-4">
        Change the order these templates appear when you send email, and edit
        the name, subject, and message for each one. The subject is used when
        you send email from project, proposal, and contact, and can only be
        changed here.
      </p>
      {loading && !list?.length ? (
        <Spinner />
      ) : (
        <Row>
          <Col md={4} className="mb-4">
            <h5 className="mb-3">Order</h5>
            {(list || []).map((template, index) => (
              <div
                key={template.key}
                className="proposal-arrange-item"
                style={{
                  cursor: "pointer",
                  borderColor: template.key === selectedKey ? "#276fb4" : undefined,
                  background: template.key === selectedKey ? "#eef5fc" : undefined,
                }}
                onClick={() => handleSelect(template)}
              >
                <span className="flex-grow-1">{template.name || template.key}</span>
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  className="ms-1 py-0 px-2"
                  disabled={index === 0 || savingOrder}
                  onClick={(e) => {
                    e.stopPropagation();
                    move(index, -1);
                  }}
                  aria-label={`Move ${template.name} up`}
                >
                  <FaArrowUp />
                </Button>
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  className="ms-1 py-0 px-2"
                  disabled={index === list.length - 1 || savingOrder}
                  onClick={(e) => {
                    e.stopPropagation();
                    move(index, 1);
                  }}
                  aria-label={`Move ${template.name} down`}
                >
                  <FaArrowDown />
                </Button>
              </div>
            ))}
          </Col>
          <Col md={8}>
            <h5 className="mb-3">Edit template</h5>
            {selected ? (
              <Form onSubmit={handleSave}>
                <Form.Group className="mb-3">
                  <Form.Label className="w-100">Template name</Form.Label>
                  <Form.Control
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={200}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <div className="text-muted mb-2" style={{ fontSize: 13 }}>
                    Merge fields are inserted into the subject or message, whichever you last clicked.
                  </div>
                  <div className="text-muted mb-2 text-start" style={{ fontSize: 13 }}>
                    {mergeFields.map((field) => (
                      <div key={field.token}>
                        {field.token} = {field.value}
                      </div>
                    ))}
                  </div>
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {MERGE_FIELD_TOKENS.map((field) => (
                      <Button
                        key={field}
                        type="button"
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => insertMergeField(field)}
                      >
                        {field}
                      </Button>
                    ))}
                  </div>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="w-100">Subject</Form.Label>
                  <Form.Control
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    onFocus={() => setActiveField("subject")}
                    maxLength={500}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="w-100">Message</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={10}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    onFocus={() => setActiveField("body")}
                  />
                </Form.Group>
                <div className="d-flex gap-2">
                  <Button type="submit" variant="primary" disabled={saving}>
                    {saving ? "Saving..." : "Save template"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline-secondary"
                    disabled={saving}
                    onClick={handleReset}
                  >
                    Reset to default
                  </Button>
                </div>
              </Form>
            ) : (
              <p className="text-muted">Select a template to edit.</p>
            )}
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default EmailTemplates;
